import type { Api } from "../api";
import type { Passkey } from "./passkey";

/**
 * The server only knows whether a person has a passkey somewhere, not on which device. This browser
 * remembers when it saved one here, so screens can say "on this device" truthfully. Per-browser
 * convenience only: storage can be blocked or cleared, and then we simply don't know.
 */
const key = (personId: string) => `pagerless.passkeyHere.${personId}`;

export function passkeyHere(personId: string): boolean {
  try {
    return globalThis.localStorage?.getItem(key(personId)) === "1";
  } catch {
    return false;
  }
}

function markPasskeyHere(personId: string): void {
  try {
    globalThis.localStorage?.setItem(key(personId), "1");
  } catch {
    // Blocked storage: the passkey is still saved on the server; we just can't remember it here.
  }
}

/** Save a passkey for this person on this device (Face ID / Touch ID prompt), and remember it here. */
export async function savePasskeyHere(api: Api, passkey: Passkey, personId: string): Promise<void> {
  const options = await api.passkeyRegisterOptions();
  await api.passkeyRegisterVerify(await passkey.register(options));
  markPasskeyHere(personId);
}

/** "This Mac", "This iPhone"…: names the device in plain words, from the browser's user agent. */
export function deviceLabel(userAgent: string): string {
  if (/iPhone/.test(userAgent)) return "This iPhone";
  if (/iPad/.test(userAgent)) return "This iPad";
  if (/Android/.test(userAgent)) return "This phone";
  if (/Macintosh|Mac OS X/.test(userAgent)) return "This Mac";
  if (/Windows/.test(userAgent)) return "This PC";
  return "This device";
}
